import { Loader, Button, Tooltip, Stepper } from '@mantine/core';
import { Badge, classNames, Icon, variants } from '@uag/react-core';
import { useTranslation } from 'react-i18next';
import { FormattedDate } from 'react-intl';

import { OnboardingRequestDetailModel } from 'api/models';
import { useGetCurrentContributor } from 'api/queries/contributors/contributors';
import { useGetIndustrialSectors } from 'api/queries/industrial-sectors/industrial-sectors';
import { Information, InformationSection } from 'components/Information/Information';
import { getBadgeVariant } from 'utils/getBadgeVariant';
import { getActiveStepIndex, getLastState, isCompleted, isDeclined } from 'utils/requestStateUtils';
import { GapiStatusHoverCard } from './GapiStatusHoverCard';

type Props = {
    request: OnboardingRequestDetailModel;
    onEditClick?: () => void;
    mode: 'Frontend' | 'Backoffice';
    fieldInsights?: Partial<Record<CompanyInformationFieldKey, React.ReactNode>>;
};

export type CompanyInformationFieldKey =
    | 'companyName'
    | 'addressStreet'
    | 'addressPostalCity'
    | 'addressCountry'
    | 'vatNr'
    | 'kvKNr'
    | 'administratorEmail'
    | 'generalCompanyEmail'
    | 'phone'
    | 'mobilePhone'
    | 'taxNr'
    | 'icoNr'
    | 'commercialRegistrationNr'
    | 'industrialSectorIdentifier'
    | 'website'
    | 'pecEmail';
type RequestStateHistoryProps = {
    request: OnboardingRequestDetailModel;
};

type InformationItemProps = {
    icon?: string;
    title: string;
    children: React.ReactNode;
    className?: string;
};

const InformationItem = ({ icon, title, children, className = '' }: InformationItemProps) => {
    if (!children) {
        return null;
    }

    return (
        <div className={`flex ${icon ? '' : 'ml-5'} ${className}`}>
            {icon && <Icon className="text-icon-default mr-1" icon={icon} />}
            <InformationSection className="grow" title={title}>
                <div className="text-text-light">{children}</div>
            </InformationSection>
        </div>
    );
};

const FieldValue = ({ children, insight }: { children: React.ReactNode; insight?: React.ReactNode }) => {
    if (!children && !insight) {
        return null;
    }

    return (
        <div className="flex flex-col gap-1">
            {children && <div>{children}</div>}
            {insight}
        </div>
    );
};

const RequestStateHistory = ({ request }: RequestStateHistoryProps) => {
    const { t } = useTranslation();
    const { stateHistory } = request;

    if (!stateHistory) {
        return null;
    }

    const lastState = getLastState(stateHistory);
    const activeState = getActiveStepIndex(stateHistory);
    const declined = isDeclined(lastState.stateType);
    const sortedStateHistory = stateHistory.sort((a, b) => {
        const dateA = new Date(a.timestamp).getTime();
        const dateB = new Date(b.timestamp).getTime();
        return dateA - dateB;
    });

    const lastIdx = sortedStateHistory.length - 1;

    return (
        <Stepper
            active={activeState}
            className="gap-base p-base flex flex-col"
            orientation="vertical"
            variant={variants.stepper.icons}
        >
            {sortedStateHistory.map((state, idx) => {
                const next = sortedStateHistory[idx + 1];

                const shouldShowOwnDate =
                    idx === lastIdx && (isCompleted(state.stateType) || isDeclined(state.stateType));

                const dateToShow = shouldShowOwnDate ? state.timestamp : next?.timestamp;

                const showDate = !!dateToShow;

                return (
                    <Stepper.Step
                        key={idx}
                        className={declined ? classNames.stepperStep.canceled : ''}
                        description={
                            showDate ? (
                                <FormattedDate
                                    day="numeric"
                                    hour="numeric"
                                    minute="numeric"
                                    month="numeric"
                                    value={new Date(dateToShow)}
                                    year="numeric"
                                />
                            ) : (
                                ''
                            )
                        }
                        icon={<Icon icon="circle" size="xs" fill />}
                        label={t(`RequestState.${state.stateType}`)}
                    />
                );
            })}
        </Stepper>
    );
};
export const CompanyInformation = ({ request, mode, onEditClick, fieldInsights }: Props) => {
    const { t } = useTranslation();
    const { data: industrialSectorsResponse, isPending: areIndustrialSectorsLoading } = useGetIndustrialSectors();
    const { data: currentContributorResponse } = useGetCurrentContributor();

    if (!request) {
        return null;
    }

    const company = request.companyData;
    const currentContributor = currentContributorResponse?.data;
    const industrialSector = industrialSectorsResponse?.data?.find(
        (sector) => sector.identifier === company.industrialSectorIdentifier,
    );

    return (
        <Information className="p-md">
            <div className="flex justify-between">
                <div className="gap-base flex">
                    <InformationSection title={company.companyName ?? ''}>
                        <div className="flex flex-col gap-1">
                            <p className="text-text-light text-sm">{company.additionalCompanyName}</p>
                            {fieldInsights?.companyName}
                        </div>
                    </InformationSection>
                    {request.currentState && mode === 'Backoffice' && (
                        <>
                            <Tooltip
                                label={<RequestStateHistory request={request} />}
                                position="bottom-start"
                                variant={variants.tooltip.custom}
                            >
                                <div className="gap-base flex flex-wrap">
                                    <Badge variant={getBadgeVariant(request.currentState)}>
                                        {t(`RequestState.${request.currentState}`)}
                                    </Badge>
                                </div>
                            </Tooltip>
                            {request.isDoka360Request && <Badge>{t('requestTypes.Doka360')}</Badge>}
                            {currentContributor?.isAdmin && <GapiStatusHoverCard requestId={request.id} />}
                        </>
                    )}
                </div>

                <div className="ml-auto">
                    {onEditClick && (
                        <Button variant={variants.button.secondary} onClick={onEditClick}>
                            {t('edit')}
                            <Icon className="ml-base" icon="edit" />
                        </Button>
                    )}
                </div>
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {company.mainAddress && (
                    <InformationItem icon="location_on" title={t('address')}>
                        <FieldValue insight={fieldInsights?.addressStreet}>
                            <p>{company.mainAddress?.street}</p>
                        </FieldValue>
                        <FieldValue insight={fieldInsights?.addressPostalCity}>
                            <p>
                                {company.mainAddress?.zipCode} {company.mainAddress?.city}
                            </p>
                        </FieldValue>
                        <FieldValue insight={fieldInsights?.addressCountry}>
                            <p>{company.mainAddress?.countryConfiguration?.name}</p>
                        </FieldValue>
                    </InformationItem>
                )}

                {company.vatNr && (
                    <InformationItem icon="credit_card" title={t('uidNumber')}>
                        <FieldValue insight={fieldInsights?.vatNr}>{company.vatNr}</FieldValue>
                    </InformationItem>
                )}

                {company.kvKNr && (
                    <InformationItem icon="credit_card" title={t('kvkNr')}>
                        <FieldValue insight={fieldInsights?.kvKNr}>{company.kvKNr}</FieldValue>
                    </InformationItem>
                )}

                {request?.administrators?.[0]?.model.email && (
                    <InformationItem icon="person" title={t('administrator')}>
                        <div className="flex flex-col gap-1">
                            <FieldValue insight={fieldInsights?.administratorEmail}>
                                <div className="text-text-light">{request.administrators[0]?.model.email}</div>
                            </FieldValue>
                            {request?.signers?.[0]?.model.email && (
                                <div className="text-text-light">
                                    <div className="text-text-default text-base">{t('contractSignatory')}</div>
                                    {request.signers[0]?.model.email}
                                </div>
                            )}
                        </div>
                    </InformationItem>
                )}
                <InformationItem icon="call" title={t('generalOfficeEmail')}>
                    <div className="flex flex-col gap-1">
                        {company.email && (
                            <FieldValue insight={fieldInsights?.generalCompanyEmail}>
                                <div className="text-text-light">{company.email}</div>
                            </FieldValue>
                        )}

                        {company.phone && (
                            <div className="text-text-light">
                                <div className="text-text-default text-base">{t('phoneNumber')}</div>
                                <FieldValue insight={fieldInsights?.phone}>{company.phone}</FieldValue>
                            </div>
                        )}

                        {company.mobilePhone && (
                            <div className="text-text-light">
                                <div className="text-text-default text-base">{t('mobilePhone')}</div>
                                <FieldValue insight={fieldInsights?.mobilePhone}>{company.mobilePhone}</FieldValue>
                            </div>
                        )}
                    </div>
                </InformationItem>
                {company.taxNr && (
                    <InformationItem title={t('taxNr')}>
                        <FieldValue insight={fieldInsights?.taxNr}>{company.taxNr}</FieldValue>
                    </InformationItem>
                )}
                {company.icoNr && (
                    <InformationItem title={t('icoNr')}>
                        <FieldValue insight={fieldInsights?.icoNr}>{company.icoNr}</FieldValue>
                    </InformationItem>
                )}
                {company.commercialRegistrationNr && (
                    <InformationItem title={t('commercialRegistrationNr')}>
                        <FieldValue insight={fieldInsights?.commercialRegistrationNr}>
                            {company.commercialRegistrationNr}
                        </FieldValue>
                    </InformationItem>
                )}
                <InformationItem title={t('industrialSectorIdentifier')}>
                    {areIndustrialSectorsLoading && <Loader />}
                    {!areIndustrialSectorsLoading && (
                        <FieldValue insight={fieldInsights?.industrialSectorIdentifier}>
                            {industrialSector?.translation}
                        </FieldValue>
                    )}
                </InformationItem>

                {company.electronicInvoiceEmail && company.electronicInvoiceType && (
                    <InformationItem title={t('electronicInvoiceEmail')}>
                        {company.electronicInvoiceEmail}

                        <div className="text-text-light">
                            <div className="text-text-default text-base">{t('electronicInvoiceType')}</div>
                            {company.electronicInvoiceType}
                        </div>
                    </InformationItem>
                )}

                {company.website && (
                    <InformationItem title={t('website')}>
                        <FieldValue insight={fieldInsights?.website}>{company.website}</FieldValue>
                    </InformationItem>
                )}
                {company.pecEmail && (
                    <InformationItem title={t('pecEmail')}>
                        <FieldValue insight={fieldInsights?.pecEmail}>{company.pecEmail}</FieldValue>
                    </InformationItem>
                )}

                <InformationItem title={t('receiverCode')}>{company.receiverCode}</InformationItem>
            </div>
        </Information>
    );
};
